import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  X as XIcon,
  Boxes,
  Search,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORY_COLORS = {
  Ores: "#e07a1f",
  Plates: "#3aa3d9",
  Crystals: "#c64bd2",
  None: "#888888",
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
};

// ============================================================================
// MapleStory-style Visual Inventory grid for a single character
// ============================================================================
// Layout matches in-game Use tab: 4 separate 4-col × 6-row blocks side-by-side
// with a visible gap, total 96 slots. Fill order is BLOCK-major (row-major
// inside each block) — so a stack of e.g. Bronze Ore × 316 produces slots
// 0..3 inside block 1 (row 0 col 0..3) and then the next item starts at the
// beginning of row 1 in the same block.
const BLOCK_COLS = 4;
const BLOCK_ROWS = 6;
const SLOTS_PER_BLOCK = BLOCK_COLS * BLOCK_ROWS; // 24
const NUM_BLOCKS = 4;
const TOTAL_SLOTS = SLOTS_PER_BLOCK * NUM_BLOCKS; // 96

const InventorySlotCell = ({ slot }) => {
  if (!slot) {
    return (
      <div className="aspect-square rounded-sm border border-[#3a1f08] bg-[#2c180a] shadow-inner" />
    );
  }
  const color = CATEGORY_COLORS[slot.category] || "#888";
  return (
    <div
      className="relative aspect-square rounded-sm border-2 bg-gradient-to-br from-[#3a230f] to-[#2a180a] shadow-inner overflow-hidden group cursor-help"
      style={{ borderColor: "#5a3a14" }}
      title={`${slot.name} × ${slot.qty}`}
      data-testid={`inv-slot-${slot.slot_index}`}
    >
      {slot.image_url ? (
        <img
          src={slot.image_url}
          alt={slot.name}
          className="absolute inset-1.5 w-[calc(100%-12px)] h-[calc(100%-12px)] object-contain"
          style={{ imageRendering: "auto" }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <div
          className="absolute inset-2 rounded-sm"
          style={{ background: color, opacity: 0.4 }}
        />
      )}
      <div className="absolute bottom-0 right-0 left-0 px-0.5 bg-black/80 text-white text-[9px] font-extrabold text-right leading-[1.1]">
        x{slot.qty}
      </div>
    </div>
  );
};

const InventoryGrid = ({ slots, capacity }) => {
  const cap = capacity || TOTAL_SLOTS;
  const slotMap = new Map();
  for (const s of slots || []) slotMap.set(s.slot_index, s);

  // Build 4 blocks; each block holds slots [b*24 .. b*24+23]
  const blocks = [];
  for (let b = 0; b < NUM_BLOCKS; b++) {
    const cells = [];
    for (let i = 0; i < SLOTS_PER_BLOCK; i++) {
      const idx = b * SLOTS_PER_BLOCK + i;
      const inCapacity = idx < cap;
      if (!inCapacity) {
        cells.push(
          <div
            key={i}
            className="aspect-square rounded-sm bg-black/60 border border-black/40"
          />
        );
      } else {
        cells.push(<InventorySlotCell key={i} slot={slotMap.get(idx)} />);
      }
    }
    blocks.push(
      <div
        key={b}
        className="rounded-md p-1 sm:p-1.5 bg-black/30 border border-[#3a1f08]"
        data-testid={`inv-block-${b}`}
      >
        <div
          className="grid gap-0.5 sm:gap-1"
          style={{
            gridTemplateColumns: `repeat(${BLOCK_COLS}, minmax(0, 1fr))`,
          }}
        >
          {cells}
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg p-2 sm:p-3 border-2 border-[#5a3a14] shadow-[0_4px_0_#1a0e05]"
      style={{
        backgroundImage:
          "linear-gradient(180deg, #5b2f12 0%, #3a1f08 40%, #2c180a 100%)",
      }}
      data-testid="visual-inventory-grid"
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        {blocks}
      </div>
    </div>
  );
};

// ============================================================================
// Detail panel for a single character (header + grid + breakdown)
// ============================================================================
const CharacterDetailPanel = ({
  characterName,
  characters,
  onChangeCharacter,
  isFullAdmin = false,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!characterName) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/orestore/characters/${encodeURIComponent(characterName)}`,
        { headers: getAuthHeaders() }
      );
      setDetail(r.data);
    } catch (e) {
      toast.error("Failed to load character inventory");
    } finally {
      setLoading(false);
    }
  }, [characterName]);

  useEffect(() => {
    load();
  }, [load]);

  const charNames = useMemo(() => (characters || []).map((c) => c.name), [characters]);
  const idx = charNames.indexOf(characterName);
  const goPrev = () => {
    if (idx > 0) onChangeCharacter(charNames[idx - 1]);
  };
  const goNext = () => {
    if (idx >= 0 && idx < charNames.length - 1) {
      onChangeCharacter(charNames[idx + 1]);
    }
  };

  const inv = detail?.inventory;

  return (
    <div className="space-y-3" data-testid="character-detail-panel">
      {/* Top bar — character selector + Used/Free counters (matches MS HUD) */}
      <div
        className="rounded-lg border-2 border-[#5a3a14] p-2.5 flex items-center gap-2 flex-wrap"
        style={{
          backgroundImage:
            "linear-gradient(180deg, #4a2810 0%, #2a180a 100%)",
        }}
      >
        <span className="text-orange-200 font-extrabold text-sm sm:text-base">
          Character:
        </span>
        <select
          value={characterName || ""}
          onChange={(e) => onChangeCharacter(e.target.value)}
          className="h-9 px-2 rounded border-2 border-[#5a3a14] bg-[#1f1208] text-orange-100 text-sm min-w-[160px]"
          data-testid="inv-char-select"
        >
          {(characters || []).map((c) => (
            <option key={c.name} value={c.name}>
              {c.name} ({c.store_category || "—"})
            </option>
          ))}
        </select>
        <Button
          variant="outline"
          size="icon"
          onClick={goPrev}
          disabled={idx <= 0}
          className="h-9 w-9"
          data-testid="inv-prev"
          title="Previous character"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={goNext}
          disabled={idx < 0 || idx >= charNames.length - 1}
          className="h-9 w-9"
          data-testid="inv-next"
          title="Next character"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>

        <div className="ml-auto flex items-center gap-3 text-sm sm:text-base font-mono font-bold">
          <span className="text-emerald-300" data-testid="inv-used">
            Used: {inv?.used_slots ?? "—"} / {inv?.capacity ?? "—"}
          </span>
          <span className="text-emerald-300" data-testid="inv-free">
            Free: {inv?.free_slots ?? "—"}
          </span>
        </div>
      </div>

      {/* Body — Inventory grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-orange-200/70">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : !detail ? (
        <div className="text-center py-8 text-orange-200/70 italic">
          No data.
        </div>
      ) : (
        <>
          <InventoryGrid slots={inv?.slots || []} capacity={inv?.capacity || 96} />

          {/* Meta info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px]">
            {isFullAdmin && (
              <MetaCell label="Account" value={detail.account || "—"} />
            )}
            <MetaCell
              label="Store category"
              value={
                <Badge
                  variant="outline"
                  style={{
                    borderColor:
                      CATEGORY_COLORS[detail.store_category] || "#666",
                    color: CATEGORY_COLORS[detail.store_category] || "#999",
                  }}
                  className="text-[10px] font-bold uppercase"
                >
                  {detail.store_category || "—"}
                </Badge>
              }
            />
            <MetaCell
              label="Playing now"
              value={
                <span
                  className={
                    detail.is_playing ? "text-emerald-300" : "text-orange-200/70"
                  }
                >
                  {detail.is_playing ? "● Yes" : "○ No"}
                </span>
              }
            />
            <MetaCell label="Last update" value={fmtDate(detail.updated_at)} />
          </div>

          {/* Breakdown table */}
          <div className="rounded-lg border-2 border-[#5a3a14] bg-[#2a1a0e] overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#3a2814] text-orange-200">
                <tr>
                  <th className="text-left px-2 py-1.5 font-bold">Item</th>
                  <th className="text-right px-2 py-1.5 font-bold">Quantity</th>
                  <th className="text-right px-2 py-1.5 font-bold">Slots used</th>
                </tr>
              </thead>
              <tbody>
                {(detail.breakdown || []).map((it) => (
                  <tr key={it.name} className="border-t border-[#5a3a14]/40">
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        {it.image_url && (
                          <img
                            src={it.image_url}
                            alt=""
                            className="w-5 h-5 object-contain"
                            style={{ imageRendering: "auto" }}
                          />
                        )}
                        <span className="font-bold text-orange-100">
                          {it.name}
                        </span>
                        <Badge
                          variant="outline"
                          style={{
                            borderColor:
                              CATEGORY_COLORS[it.category] || "#666",
                            color: CATEGORY_COLORS[it.category] || "#999",
                          }}
                          className="text-[9px] px-1 py-0"
                        >
                          {it.category || "?"}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-amber-300">
                      {it.qty.toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-orange-200/70">
                      {it.stacks_used}
                    </td>
                  </tr>
                ))}
                {(!detail.breakdown || detail.breakdown.length === 0) && (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center py-4 text-orange-200/60 italic"
                    >
                      Empty inventory.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const MetaCell = ({ label, value }) => (
  <div className="rounded-md border border-[#5a3a14] bg-[#1f1208] px-2 py-1.5">
    <div className="text-[9px] uppercase tracking-wider text-orange-200/60 font-bold">
      {label}
    </div>
    <div className="text-orange-100 font-bold mt-0.5">{value}</div>
  </div>
);

// ============================================================================
// Standalone modal: opens visual inventory from Stock tab
// ============================================================================
export const VisualInventoryModal = ({ open, onClose, initialCharacter }) => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(initialCharacter || null);
  const [viewerRole, setViewerRole] = useState("orestore_admin");

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin/orestore/characters`, {
          headers: getAuthHeaders(),
        });
        if (cancel) return;
        const list = r.data?.characters || [];
        setCharacters(list);
        setViewerRole(r.data?.viewer_role || "orestore_admin");
        if (!selected && list.length > 0) {
          setSelected(initialCharacter || list[0].name);
        }
      } catch (e) {
        if (!cancel) toast.error("Failed to load characters");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="visual-inventory-modal"
    >
      <div
        className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-lg border-4 border-[#5a3a14] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundImage:
            "linear-gradient(180deg, #3a230f 0%, #2a180a 100%)",
        }}
      >
        {/* Header bar — match in-game window header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-3 py-2 border-b-2 border-[#5a3a14] bg-gradient-to-b from-[#5b2f12] to-[#3a1f08]">
          <div className="flex items-center gap-2 font-extrabold text-orange-100">
            <Boxes className="w-4 h-4 text-amber-300" />
            <span className="italic">Visual Inventory</span>
          </div>
          <button
            onClick={onClose}
            className="text-orange-100 hover:text-amber-300 transition"
            aria-label="Close"
            data-testid="visual-inventory-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-3 sm:p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-orange-200/70">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : characters.length === 0 ? (
            <div className="text-center py-12 text-orange-200/70 italic">
              No OreStore characters configured.
            </div>
          ) : (
            <CharacterDetailPanel
              characterName={selected}
              characters={characters}
              onChangeCharacter={setSelected}
              isFullAdmin={viewerRole === "admin"}
            />
          )}

          <div className="text-[11px] text-orange-200/60 mt-3 italic">
            Hover slot for item info • Each stack holds up to 100 (e.g. Bronze
            Ore × 316 = 100 + 100 + 100 + 16 → 4 slots).
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Dedicated "Characters" tab content (lists every character + drill into one)
// ============================================================================
export const CharactersPanel = () => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [viewerRole, setViewerRole] = useState("orestore_admin");
  const [savingVis, setSavingVis] = useState(null);
  const [showHidden, setShowHidden] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/orestore/characters`, {
        headers: getAuthHeaders(),
      });
      const list = r.data?.characters || [];
      setCharacters(list);
      setViewerRole(r.data?.viewer_role || "orestore_admin");
      if (!selected && list.length > 0) setSelected(list[0].name);
    } catch (e) {
      toast.error("Failed to load characters");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isFullAdmin = viewerRole === "admin";

  const toggleHidden = useCallback(
    async (charName, nextHidden) => {
      setSavingVis(charName);
      try {
        await axios.put(
          `${API_URL}/api/admin/orestore/characters/${encodeURIComponent(charName)}/visibility`,
          { hidden_from_orestore_admin: nextHidden },
          { headers: getAuthHeaders() }
        );
        setCharacters((prev) =>
          prev.map((c) =>
            c.name === charName
              ? { ...c, hidden_from_orestore_admin: nextHidden }
              : c
          )
        );
        toast.success(
          nextHidden
            ? `${charName} hidden from Ore Admins`
            : `${charName} now visible to Ore Admins`
        );
      } catch (e) {
        toast.error(
          e.response?.data?.detail || "Failed to update visibility"
        );
      } finally {
        setSavingVis(null);
      }
    },
    []
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = characters;
    if (categoryFilter !== "all") {
      list = list.filter(
        (c) => (c.store_category || "None") === categoryFilter
      );
    }
    if (q) {
      list = list.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.account || "").toLowerCase().includes(q)
      );
    }
    if (isFullAdmin && !showHidden) {
      list = list.filter((c) => !c.hidden_from_orestore_admin);
    }
    return list;
  }, [characters, search, categoryFilter, isFullAdmin, showHidden]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-orange-200/70">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="characters-panel">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={
              isFullAdmin
                ? "Search characters or accounts…"
                : "Search characters…"
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="char-search"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-9 px-2 rounded border border-input bg-background text-sm"
          data-testid="char-cat-filter"
        >
          <option value="all">All categories</option>
          <option value="Ores">Ores</option>
          <option value="Plates">Plates</option>
          <option value="Crystals">Crystals</option>
          <option value="None">Storage / None</option>
        </select>
        {isFullAdmin && (
          <label className="text-xs flex items-center gap-1.5 px-2 py-1.5 rounded border border-input bg-background cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              data-testid="show-hidden-toggle"
            />
            Show hidden
          </label>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          data-testid="char-refresh"
        >
          Refresh
        </Button>
      </div>

      {isFullAdmin && (
        <div className="rounded-md border border-amber-700/50 bg-amber-900/20 px-3 py-2 text-[12px] text-amber-100">
          As <strong>Admin</strong>, you can mark any character as
          <Badge variant="outline" className="mx-1 border-amber-400 text-amber-200">Hidden</Badge>
          — Ore Admins will not see them in the Stock or Characters tabs. Account names are also hidden from Ore Admins.
        </div>
      )}

      <div className="grid lg:grid-cols-[300px_1fr] gap-3">
        {/* Left rail — character list */}
        <div className="rounded-lg border-2 border-[#5a3a14] bg-[#1f1208] max-h-[640px] overflow-y-auto">
          {filtered.map((c) => {
            const color = CATEGORY_COLORS[c.store_category] || "#666";
            const isSel = c.name === selected;
            const usedPct = c.capacity
              ? Math.min(100, Math.round((c.used_slots / c.capacity) * 100))
              : 0;
            const hidden = !!c.hidden_from_orestore_admin;
            return (
              <div
                key={c.name}
                className={`w-full text-left px-3 py-2 border-b border-[#5a3a14]/50 transition flex items-center gap-2 ${
                  isSel
                    ? "bg-[#3a2814]"
                    : "bg-transparent hover:bg-[#2a180a]"
                } ${hidden ? "opacity-60" : ""}`}
                data-testid={`char-row-${c.name}`}
              >
                <button
                  type="button"
                  onClick={() => setSelected(c.name)}
                  className="flex-1 flex items-center gap-2 text-left"
                >
                  <div
                    className="w-2 h-10 rounded-sm flex-shrink-0"
                    style={{ background: color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-orange-100 truncate flex items-center gap-1">
                      {c.name}
                      {c.is_playing ? (
                        <span className="text-emerald-300 text-xs">●</span>
                      ) : null}
                      {hidden && (
                        <Badge
                          variant="outline"
                          className="border-amber-500 text-amber-300 text-[9px] px-1 py-0"
                        >
                          hidden
                        </Badge>
                      )}
                    </div>
                    <div className="text-[10px] text-orange-200/60 truncate">
                      {isFullAdmin ? (c.account || "—") : "—"} •{" "}
                      {c.store_category || "None"}
                    </div>
                    <div className="mt-1 h-1 rounded bg-[#1a0e05] overflow-hidden">
                      <div
                        className="h-full rounded"
                        style={{
                          width: usedPct + "%",
                          background: color,
                          opacity: 0.85,
                        }}
                      />
                    </div>
                    <div className="text-[10px] text-orange-200/70 mt-0.5 font-mono">
                      {c.used_slots}/{c.capacity} slots •{" "}
                      {c.total_units.toLocaleString()} units
                    </div>
                  </div>
                </button>
                {isFullAdmin && (
                  <label
                    className="flex flex-col items-center gap-0.5 text-[9px] uppercase font-bold text-orange-200/80 cursor-pointer select-none px-1"
                    title="Hide this character from Ore Admins"
                  >
                    <input
                      type="checkbox"
                      checked={hidden}
                      disabled={savingVis === c.name}
                      onChange={(e) => toggleHidden(c.name, e.target.checked)}
                      data-testid={`char-hide-${c.name}`}
                    />
                    Hide
                  </label>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-6 text-orange-200/60 italic text-sm">
              No characters match the filter.
            </div>
          )}
        </div>

        {/* Right — detail panel */}
        <div>
          {selected ? (
            <CharacterDetailPanel
              characterName={selected}
              characters={characters}
              onChangeCharacter={setSelected}
              isFullAdmin={isFullAdmin}
            />
          ) : (
            <div className="text-center py-16 text-orange-200/70 italic">
              Select a character to view their inventory.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisualInventoryModal;
